
import { CampingSite } from '@/services/campingSite/types';
import { AmenityRenderer } from './AmenityRenderer';

interface InfoWindowContentProps {
  site: CampingSite;
}

export const InfoWindowContent = ({ site }: InfoWindowContentProps) => {
  const amenityRenderer = AmenityRenderer({ site });
  
  // Format location name for display
  const locationDisplay = site.location === 'mountain-view' ? 'Mountain View' : 
                        site.location === 'sheep-creek' ? 'Sheep Creek' : 'Wild Horse';
  
  // Use a default image path since the CampingSite interface doesn't have an image property
  const defaultImagePath = '/lovable-uploads/3bdf8f99-6c1c-425a-9c15-6cc49b6cc262.png';
  
  const status = (site as any).status ?? 'active';
  const isComingSoon = status === 'coming_soon';
  const statusMessage = (site as any).statusMessage as string | null | undefined;

  const createContentHTML = () => {
    const comingSoonBadge = isComingSoon
      ? `<div class="absolute top-2 left-1/2 -translate-x-1/2 bg-amber-500 text-white rounded-full px-2 py-1 text-xs font-medium shadow-md">Coming Soon</div>`
      : '';

    const statusMessageBlock = isComingSoon && statusMessage
      ? `<div class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">${statusMessage}</div>`
      : '';

    const actionButton = isComingSoon
      ? `<button
            disabled
            class="flex-1 bg-gray-400 text-white text-sm rounded-md px-3 py-2 ml-2 cursor-not-allowed font-medium"
          >
            Coming Soon
          </button>`
      : `<button 
            id="book-site-${site.id}" 
            class="flex-1 bg-tribal-primary text-white text-sm rounded-md px-3 py-2 ml-2 hover:bg-tribal-primary/90 transition-colors font-medium"
          >
            Book Now
          </button>`;

    return `
      <div class="camp-site-card">
        <div class="relative">
          <img 
            src="${defaultImagePath}"
            alt="${site.siteNumber || 'Campsite'}"
            class="w-full h-32 object-cover rounded-t-lg"
            onerror="this.src='/lovable-uploads/3bdf8f99-6c1c-425a-9c15-6cc49b6cc262.png';"
          />
          <div class="absolute top-2 left-2 bg-white/90 rounded-full px-2 py-1 text-xs font-medium">
            ${site.siteNumber || 'Campsite'}
          </div>
          <div class="absolute top-2 right-2 bg-white/90 rounded-full px-2 py-1 text-xs font-medium text-tribal-primary">
            $${site.pricePerNight}/night
          </div>
          ${comingSoonBadge}
        </div>
        <div class="p-3">
          <div class="flex items-center gap-1 mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-tribal-primary"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
            <h3 class="text-sm font-semibold">${locationDisplay}</h3>
          </div>
          
          ${statusMessageBlock}
          ${amenityRenderer.renderAmenityIndicators()}
          
          <div class="flex justify-between items-center mt-2">
            <button 
              id="close-site-${site.id}" 
              class="flex items-center justify-center bg-tribal-primary text-white w-8 h-8 rounded-full text-xs shadow-md hover:bg-tribal-primary/90 transition-colors"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
            ${actionButton}
          </div>
        </div>
      </div>
    `;
  };

  return { createContentHTML };
};
